import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Button, Badge, ScrollArea, toast, Input } from '../index';
import {
  ActiveLoginService,
  type ActiveLoginSession,
  UserService,
  CommissionService,
} from '@repo/api-sdk';
import {
  Laptop,
  Smartphone,
  Globe,
  Trash2,
  LogOut,
  ShieldAlert,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Check,
  X,
  Target,
  Clock,
  AlertTriangle,
  Percent,
  GraduationCap,
  ArrowUp,
  ArrowDown,
  Calendar,
} from 'lucide-react';
import { useAuth } from '@repo/auth';
import { DialogIds, useDialog } from '../hooks/use-dialog';

export const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, portal } = useAuth();
  const queryClient = useQueryClient();
  const { open: openDeleteConfirm } = useDialog(DialogIds.DELETE_CONFIRM);

  // Extract Sales Role ID if available
  const salesRoleId = (user as any)?.roles?.find(
    (r: any) => r.role === 'Sales' || r.role === 'SalesManager'
  )?._id;

  const { data: progressResponse } = useQuery({
    queryKey: ['commission-progress', salesRoleId],
    queryFn: () => CommissionService.getAgentProgress(salesRoleId!),
    enabled: portal === 'sales' && !!salesRoleId,
  });
  const progressData = progressResponse;

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Password validation function
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push(t('profile.passwordMinLength'));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('profile.passwordUppercase'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('profile.passwordLowercase'));
    }
    if (!/[0-9]/.test(password)) {
      errors.push(t('profile.passwordNumber'));
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('profile.passwordSpecial'));
    }
    return errors;
  };

  // Calculate password strength
  const getPasswordStrength = (
    password: string
  ): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };

    const errors = validatePassword(password);
    const strength = ((5 - errors.length) / 5) * 100;

    if (strength === 100)
      return { strength, label: t('profile.passwordStrong'), color: 'bg-green-600' };
    if (strength >= 60)
      return { strength, label: t('profile.passwordMedium'), color: 'bg-yellow-600' };
    return { strength, label: t('profile.passwordWeak'), color: 'bg-red-600' };
  };

  const { data: sessionsResponse, isLoading } = useQuery({
    queryKey: ['active-sessions'],
    queryFn: ActiveLoginService.getActiveSessions,
  });

  // Sort sessions: current session first, then by last activity
  const sortedSessions = sessionsResponse?.data
    ? [...sessionsResponse.data].sort((a, b) => {
        if (a.isCurrent) return -1;
        if (b.isCurrent) return 1;
        return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
      })
    : [];

  const terminateSessionMutation = useMutation({
    mutationFn: ActiveLoginService.terminateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: UserService.changePassword,
    onSuccess: (data) => {
      toast.success(data.message || t('profile.passwordChanged'));
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors([]);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message;
      // Handle array of messages from backend
      const displayMessage = Array.isArray(errorMessage)
        ? errorMessage.join(', ')
        : errorMessage || t('common.error');
      toast.error(displayMessage);
    },
  });

  const terminateAllOtherMutation = useMutation({
    mutationFn: ActiveLoginService.terminateAllOtherSessions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  const getDeviceIcon = (deviceType: string) => {
    if (
      deviceType?.toLowerCase().includes('mobile') ||
      deviceType?.toLowerCase().includes('android') ||
      deviceType?.toLowerCase().includes('ios')
    ) {
      return <Smartphone className="h-5 w-5" />;
    }
    return <Laptop className="h-5 w-5" />;
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateString));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{t('profile.title')}</h2>
          <p className="text-sm md:text-base text-muted-foreground">{t('profile.description')}</p>
        </div>
      </div>

      {/* User Info Card */}
      <Card className="shadow-sm overflow-hidden">
        <CardHeader className="bg-linear-to-r from-primary/5 via-primary/3 to-transparent border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
              <Laptop className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">{t('profile.personalInfo')}</h3>
              <p className="text-sm text-muted-foreground">{t('profile.personalInfoDesc')}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Main Info */}
            <div className="space-y-5">
              <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-lg font-semibold text-primary">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('common.name')}
                  </span>
                  <p className="text-base font-semibold truncate">{user?.name || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Globe className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('common.email')}
                  </span>
                  <p className="text-base font-medium truncate">{user?.email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Smartphone className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('common.phone')}
                  </span>
                  <p className="text-base font-medium">{(user as any)?.phone || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Right Column - Status & Meta */}
            <div className="space-y-5">
              <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground block mb-3">
                  {t('common.role')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {user?.roles && Array.isArray(user.roles) ? (
                    user.roles.map((roleObj: any, index: number) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className={`px-3 py-1 gap-1.5 border ${
                          roleObj.isActive
                            ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                            : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                        }`}
                      >
                        {roleObj.role || roleObj}
                        {roleObj.isActive !== undefined ? (
                          roleObj.isActive ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <X className="h-3.5 w-3.5" />
                          )
                        ) : null}
                      </Badge>
                    ))
                  ) : (
                    <Badge className="px-3 py-1">{(user as any)?.role || 'N/A'}</Badge>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-linear-to-br from-muted/50 to-muted/30 border border-border/50">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground block mb-1">
                      {t('profile.lastLogin')}
                    </span>
                    <p className="text-sm font-semibold">
                      {(user as any)?.lastLogin ? formatDate((user as any).lastLogin) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground block mb-1">
                      {t('profile.accountCreated')}
                    </span>
                    <p className="text-sm font-semibold">
                      {(user as any)?.createdAt ? formatDate((user as any).createdAt) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission Rules Section (Sales Portal Only) */}
      {portal === 'sales' && progressData?.ruleProgress && progressData.ruleProgress.length > 0 && (
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="bg-linear-to-r from-primary/5 via-primary/3 to-transparent border-b pb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">
                  {t('sales.dashboard.appliedRules', { defaultValue: 'Applied Commission Rules' })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('sales.dashboard.rulesDescription', {
                    defaultValue: 'Targets and rules affecting your commission',
                  })}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {progressData.ruleProgress.map((rule: any) => {
                const isGroup = rule.ruleType === 'group_fill';
                const statusConfig = {
                  met: {
                    icon: CheckCircle2,
                    label: t('sales.dashboard.ruleStatus.met', { defaultValue: 'Target Met' }),
                    color: 'text-emerald-600 dark:text-emerald-400',
                    bg: 'bg-emerald-500/10',
                    border: 'border-emerald-500/30',
                  },
                  in_progress: {
                    icon: Clock,
                    label: t('sales.dashboard.ruleStatus.inProgress', {
                      defaultValue: 'In Progress',
                    }),
                    color: 'text-blue-600 dark:text-blue-400',
                    bg: 'bg-blue-500/10',
                    border: 'border-blue-500/30',
                  },
                  not_met: {
                    icon: AlertTriangle,
                    label: t('sales.dashboard.ruleStatus.notMet', { defaultValue: 'Not Met' }),
                    color: 'text-red-600 dark:text-red-400',
                    bg: 'bg-red-500/10',
                    border: 'border-red-500/30',
                  },
                }[rule.status as string] || {
                  icon: Clock,
                  label: rule.status,
                  color: 'text-muted-foreground',
                  bg: 'bg-muted',
                  border: 'border-border',
                };
                const StatusIcon = statusConfig.icon;

                return (
                  <Card
                    key={rule.ruleId}
                    className={`relative overflow-hidden shadow-sm border ${statusConfig.border}`}
                  >
                    <div className={`absolute inset-0 ${statusConfig.bg} opacity-30`} />
                    <CardContent className="p-4 space-y-4 relative z-10">
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1 min-w-0 flex-1">
                          <div
                            className="font-bold text-foreground truncate text-sm"
                            title={rule.ruleName}
                          >
                            {rule.ruleName}
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border} shrink-0 text-[10px] h-5`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-[10px]">
                        <Badge variant="outline" className="gap-1 h-5">
                          {isGroup ? (
                            <>
                              <Percent className="w-2.5 h-2.5" />{' '}
                              {t('sales.dashboard.ruleType.groupFill', { defaultValue: 'Fill' })}
                            </>
                          ) : (
                            <>
                              <GraduationCap className="w-2.5 h-2.5" />{' '}
                              {t('sales.dashboard.ruleType.studentCount', {
                                defaultValue: 'Students',
                              })}
                            </>
                          )}
                        </Badge>
                        <span className="text-muted-foreground">
                          {t('sales.dashboard.target', { defaultValue: 'Target' })}:{' '}
                          <span className="font-semibold text-foreground">
                            {isGroup
                              ? `${rule.targetFillPercentage}%`
                              : `${rule.targetStudentCount}`}
                          </span>
                        </span>
                      </div>

                      {rule.targetDate && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {t('sales.dashboard.deadline', { defaultValue: 'Deadline' })}:{' '}
                            <span className="font-medium text-foreground">
                              {new Date(rule.targetDate).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-3 pt-2 border-t border-border/50 text-[10px]">
                        {rule.targetMetAction?.commissionValue != null && (
                          <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <ArrowUp className="w-2.5 h-2.5" />
                            <span className="font-semibold">
                              +{rule.targetMetAction.commissionValue}%
                            </span>
                          </div>
                        )}
                        {rule.targetNotMetAction?.type === 'reduce' &&
                          rule.targetNotMetAction?.commissionValue != null && (
                            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDown className="w-2.5 h-2.5" />
                              <span className="font-semibold">
                                -{rule.targetNotMetAction.commissionValue}%
                              </span>
                            </div>
                          )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Change Password Card */}
      <Card className="shadow-sm overflow-hidden">
        <CardHeader className="bg-linear-to-r from-orange-500/5 via-orange-500/3 to-transparent border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-orange-500/10 flex items-center justify-center border-2 border-orange-500/20">
              <ShieldAlert className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">{t('profile.changePassword')}</h3>
              <p className="text-sm text-muted-foreground">{t('profile.changePasswordDesc')}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();

              // Check for empty fields
              if (
                !passwordForm.currentPassword ||
                !passwordForm.newPassword ||
                !passwordForm.confirmPassword
              ) {
                toast.error(t('profile.fillAllFields'));
                return;
              }

              // Validate passwords
              const errors = validatePassword(passwordForm.newPassword);
              if (errors.length > 0) {
                toast.error(t('profile.passwordRequirements'));
                return;
              }

              if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                toast.error(t('profile.passwordMismatch'));
                return;
              }

              changePasswordMutation.mutate({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                confirmPassword: passwordForm.confirmPassword,
              });
            }}
            className="space-y-5 max-w-md"
          >
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="text-sm font-medium block mb-2">
                {t('profile.currentPassword')}
              </label>
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                required
                placeholder="••••••••"
                rightElement={
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({ ...showPasswords, current: !showPasswords.current })
                    }
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              />
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="text-sm font-medium block mb-2">
                {t('profile.newPassword')}
              </label>
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                value={passwordForm.newPassword}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                  setPasswordErrors(validatePassword(e.target.value));
                }}
                required
                placeholder="••••••••"
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              />

              {/* Password Strength Indicator */}
              {passwordForm.newPassword && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t('profile.passwordStrength')}</span>
                    <span
                      className={`font-medium ${getPasswordStrength(passwordForm.newPassword).color.replace('bg-', 'text-')}`}
                    >
                      {getPasswordStrength(passwordForm.newPassword).label}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getPasswordStrength(passwordForm.newPassword).color}`}
                      style={{
                        width: `${getPasswordStrength(passwordForm.newPassword).strength}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Password Requirements */}
              {passwordForm.newPassword && (
                <div className="mt-3 space-y-1.5">
                  {[
                    {
                      test: passwordForm.newPassword.length >= 8,
                      label: t('profile.passwordMinLength'),
                    },
                    {
                      test: /[A-Z]/.test(passwordForm.newPassword),
                      label: t('profile.passwordUppercase'),
                    },
                    {
                      test: /[a-z]/.test(passwordForm.newPassword),
                      label: t('profile.passwordLowercase'),
                    },
                    {
                      test: /[0-9]/.test(passwordForm.newPassword),
                      label: t('profile.passwordNumber'),
                    },
                    {
                      test: /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword),
                      label: t('profile.passwordSpecial'),
                    },
                  ].map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      {req.test ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <span
                        className={
                          req.test ? 'text-green-600 font-medium' : 'text-muted-foreground'
                        }
                      >
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="text-sm font-medium block mb-2">
                {t('profile.confirmPassword')}
              </label>
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                required
                placeholder="••••••••"
                rightElement={
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })
                    }
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              />
              {passwordForm.confirmPassword &&
                passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                    <XCircle className="h-3.5 w-3.5" />
                    {t('profile.passwordMismatch')}
                  </p>
                )}
              {passwordForm.confirmPassword &&
                passwordForm.newPassword === passwordForm.confirmPassword && (
                  <p className="mt-2 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {t('profile.passwordMatch')}
                  </p>
                )}
            </div>

            <Button
              type="submit"
              disabled={
                changePasswordMutation.isPending ||
                passwordErrors.length > 0 ||
                passwordForm.newPassword !== passwordForm.confirmPassword
              }
              className="cursor-pointer"
            >
              {changePasswordMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('profile.updatePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Active Sessions Card */}
      <Card className="shadow-sm">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b bg-muted/10 pb-4">
          <div>
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-primary" />
              {t('profile.activeSessions')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{t('profile.sessionsDescription')}</p>
          </div>
          {sessionsResponse?.data && sessionsResponse.data.length > 1 && (
            <Button
              variant="destructive"
              className="w-full sm:w-auto shadow-sm cursor-pointer"
              onClick={() => {
                openDeleteConfirm({
                  title: t('profile.terminateAllOther'),
                  description: t('profile.confirmTerminateAll'),
                  onConfirm: () => terminateAllOtherMutation.mutateAsync(),
                });
              }}
              disabled={terminateAllOtherMutation.isPending}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('profile.terminateAllOther')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea>
            <div className="flex flex-col">
              {isLoading ? (
                <p className="text-center py-8 text-muted-foreground">{t('common.loading')}</p>
              ) : (
                sortedSessions.map((session: ActiveLoginSession) => (
                  <div
                    key={session.id}
                    className={`flex flex-col sm:flex-row items-start justify-between p-4 sm:p-5 border-b border-border last:border-0 transition-colors hover:bg-muted/30 ${session.isCurrent ? 'bg-primary/5 hover:bg-primary/10' : ''}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-5 w-full sm:w-auto">
                      <div
                        className={`p-3 rounded-full flex items-center justify-center shrink-0 ${session.isCurrent ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}
                      >
                        {getDeviceIcon(session.deviceType || session.os)}
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-semibold text-base text-foreground">
                            {session.deviceName || session.os || 'Unknown Device'}
                          </p>
                          {session.isCurrent && (
                            <Badge
                              variant="default"
                              className="bg-green-600 hover:bg-green-700 shadow-xs"
                            >
                              {t('profile.currentSession')}
                            </Badge>
                          )}
                          {session.portal && (
                            <Badge
                              variant="secondary"
                              className="capitalize bg-blue-50 text-blue-700 border-blue-200"
                            >
                              {session.portal.replace('-', ' ')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p className="flex items-center gap-2 flex-wrap">
                            <Globe className="h-3.5 w-3.5 shrink-0" />
                            <span className="font-medium text-foreground/80">
                              {session.browser}
                            </span>{' '}
                            on {session.os}
                            <span className="text-muted-foreground/40 px-1 hidden sm:inline">
                              •
                            </span>
                            <span className="block sm:inline w-full sm:w-auto mt-1 sm:mt-0">
                              {session.ipAddress}
                            </span>
                          </p>
                          <p className="text-xs flex items-center gap-1.5 pt-0.5 flex-wrap">
                            {session.location !== 'Unknown' && (
                              <>
                                <span className="font-medium text-foreground/70">
                                  {session.location}
                                </span>
                                <span className="text-muted-foreground/40">•</span>
                              </>
                            )}
                            <span>
                              {t('profile.lastActive')}: {formatDate(session.lastActivityAt)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {!session.isCurrent && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20 mt-4 sm:mt-0 shadow-xs cursor-pointer"
                        onClick={() => {
                          openDeleteConfirm({
                            title: t('common.revoke'),
                            description: t('profile.confirmTerminate'),
                            itemName: `${session.browser} (${session.os})`,
                            onConfirm: () => terminateSessionMutation.mutateAsync(session.id),
                          });
                        }}
                        disabled={terminateSessionMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('common.revoke')}
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};
